import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, ArrowRight, Receipt, HandHeart, Sparkle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeepDiveSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const [reportId, setReportId] = useState(null);
  const [amount, setAmount] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      navigate("/career-deepdive");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/career-deepdive/status/${sessionId}`);
        if (cancelled) return;
        if (data.amount_total) setAmount(data.amount_total);

        if (data.payment_status === "paid" && data.report_id) {
          setStatus("paid");
          setReportId(data.report_id);
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        if (attempts.current >= 15) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts.current >= 15) setStatus("failed");
        else setTimeout(poll, 2000);
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <main className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="deepdive-success-page">
      {status === "checking" && (
        <div className="text-center">
          <div className="inline-flex h-16 w-16 rounded-full bg-[#0033A0]/10 items-center justify-center">
            <Sparkle size={28} weight="fill" className="text-[#0033A0] animate-pulse" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mt-6">
            Finalizing your purchase…
          </h1>
          <p className="text-slate-600 mt-3 leading-relaxed">
            We{`'`}re confirming your payment with Stripe and generating your branch-specific Career Deep Dive. This usually takes 10–30 seconds.
          </p>
          <div className="mt-8 flex justify-center gap-1">
            <span className="dot inline-block h-2.5 w-2.5 rounded-full bg-[#0033A0]" />
            <span className="dot inline-block h-2.5 w-2.5 rounded-full bg-[#0033A0]" />
            <span className="dot inline-block h-2.5 w-2.5 rounded-full bg-[#0033A0]" />
          </div>
        </div>
      )}

      {status === "paid" && (
        <div className="text-center" data-testid="success-confirmation">
          <div className="inline-flex h-20 w-20 rounded-full bg-emerald-100 items-center justify-center">
            <CheckCircle size={42} weight="fill" className="text-emerald-600" />
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-black tracking-tight text-slate-900 mt-6">
            Payment confirmed.
          </h1>
          <p className="text-slate-700 mt-4 leading-relaxed max-w-xl mx-auto">
            Your Career Deep Dive is ready. Thank you for supporting Boot To Benefit — half of the net profit from this report will be donated to veteran-support organizations.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-xl mx-auto text-left">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <Receipt size={20} weight="duotone" className="text-[#0033A0]" />
              <p className="text-xs text-slate-500 mt-2">Amount paid</p>
              <p className="font-display font-bold text-slate-900">
                ${amount != null ? (amount / 100).toFixed(2) : "3.00"} USD
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <Sparkle size={20} weight="duotone" className="text-[#0033A0]" />
              <p className="text-xs text-slate-500 mt-2">Status</p>
              <p className="font-display font-bold text-slate-900">Delivered</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <HandHeart size={20} weight="duotone" className="text-[#D90429]" />
              <p className="text-xs text-slate-500 mt-2">Veteran impact</p>
              <p className="font-display font-bold text-slate-900">+50% net profit</p>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link
              to={`/career-deepdive/reports/${reportId}`}
              data-testid="open-report-btn"
              className="inline-flex items-center gap-2 bg-[#0033A0] hover:bg-[#002277] text-white font-semibold py-3 px-7 rounded-md transition-colors"
            >
              Open my report <ArrowRight size={16} weight="bold" />
            </Link>
            <Link to="/dashboard" data-testid="back-to-dashboard-btn" className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 border border-slate-300 text-slate-900 font-semibold py-3 px-7 rounded-md transition-colors">
              My dashboard
            </Link>
          </div>
        </div>
      )}

      {(status === "failed" || status === "timeout") && (
        <div className="text-center" data-testid="deepdive-failed">
          <h1 className="font-display text-3xl font-bold text-slate-900">
            {status === "timeout" ? "Still processing…" : "Something went wrong"}
          </h1>
          <p className="text-slate-700 mt-3 max-w-xl mx-auto leading-relaxed">
            {status === "timeout"
              ? "Your payment may still be confirming. Check your dashboard in a minute or contact support."
              : "We couldn't confirm your payment. If you were charged, please contact support — we'll fix it and never double-charge."}
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <Link to="/dashboard" className="inline-flex items-center gap-2 bg-[#0033A0] hover:bg-[#002277] text-white font-semibold py-3 px-6 rounded-md">
              Go to dashboard
            </Link>
            <Link to="/career-deepdive" className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-900 font-semibold py-3 px-6 rounded-md">
              Try again
            </Link>
          </div>
        </div>
      )}
    </main>
  );
}
